<template>
  <div :class="$style.widget" data-test="lister-branding-box">
    <div
      v-if="listerBranding.logoUrl"
      :class="[$style.logo, { [$style.hasFallbackImage]: imageLoadError }]"
    >
      <img
        v-if="!imageLoadError"
        :src="listerBranding.logoUrl"
        :alt="listerBrandingLegalName"
        @error="onLoadImageError"
      />
      <img v-else :src="errorImage" :alt="listerBrandingLegalName" />
    </div>
    <div
      v-if="listerBranding.legalName"
      :class="[$style.details, { [$style.hasPartnerBadge]: partnerBadge }]"
    >
      <div v-if="!partnerBadge" :class="$style.title">{{ t('listerBranding.title') }}</div>
      <div :class="$style.nameRow">
        <div :class="$style.name">{{ listerBranding.legalName }}</div>
        <PartnerBadgeComponent
          v-if="partnerBadge"
          :badge="partnerBadge"
          :class="$style.partnerBadge"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ListerBranding } from '@smg-real-estate/hg-search-libs';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import PartnerBadgeComponent from '@/components/PartnerBadge/PartnerBadge.vue';
import type { PartnerBadge } from '@/helpers/partnerBadge/getPartnerBadge';
import { isPlatformIs24 } from '@/helpers/platformDetection';

const { t } = useI18n();

defineOptions({
  name: 'ListerBrandingBox',
});

interface Props {
  listerBranding: ListerBranding;
  partnerBadge?: PartnerBadge | null;
}
const props = defineProps<Props>();

const imageLoadError = ref(false);
const errorImage = ref<string | undefined>(undefined);

const listerBrandingLegalName = computed(() => {
  return props.listerBranding.legalName || '';
});

onMounted(() => {
  setErrorImage();
});

function onLoadImageError() {
  imageLoadError.value = true;
}
async function setErrorImage() {
  const image = await import(
    isPlatformIs24()
      ? '@/assets/customer-logo-fallback-is24.png'
      : '@/assets/customer-logo-fallback.png'
  );
  errorImage.value = image.default;
}
</script>

<style module lang="scss">
@import '@/assets/variables';

$lister-logo-width: 120px;
$lister-logo-height: 48px;
$fallback-box-height: 86px;

.widget {
  display: flex;
  align-items: center;
  margin-bottom: $space-150;
  border: 1px solid $color-grey-50;
  border-radius: $border-radius;

  .logo {
    padding: $space-100 $space-50;
    border-right: 1px solid $color-grey-50;

    img {
      display: block;
      width: $lister-logo-width;
      height: $lister-logo-height;
      object-fit: contain;
    }
  }

  .hasFallbackImage {
    height: $fallback-box-height;
    padding: 0;

    img {
      width: 100%;
      height: 100%;
    }
  }

  .details {
    padding: $space-75;
    overflow-wrap: anywhere;

    &.hasPartnerBadge {
      flex: 1;
      padding: $space-50 $space-75;

      .name {
        line-height: 24px;
      }
    }
  }

  .title {
    @include body-3;
  }

  .name {
    @include heading-4;
  }

  .nameRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-50;
  }

  .partnerBadge {
    flex-shrink: 0;
    width: $partner-badge-size;
    height: $partner-badge-size;
  }
}
</style>
